import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import CoordinationDashboard from './CoordinationDashboard';
import MyDay from './MyDay';
import ScCalendarApp from './calendar/ScCalendarApp';
import MailApp from '../Mail/MailApp';
// SC Workflow Specification v1 screens (kit/ = the SC design kit on Radix + TanStack).
import ScSettings from './workflow/ScSettings';
import ScOverview from './workflow/ScOverview';
import ScHours from './workflow/ScHours';
import ScReferrals from './workflow/ScReferrals';
import ScClaims from './workflow/ScClaims';
import { LogTimeProvider } from './workflow/LogTimeProvider';
import { PageHeader, useLoad } from './kit';
import Caseload from './file/Caseload';
import ParticipantFile from './file/ParticipantFile';
import { SettingsModal, CoordLoginsModal } from './file/modals';

// Support Coordination. The sidebar picks the screen (?view=, Overview by default); a participant's
// file lives at /support-coordination/:id with its section in ?tab=. Everything sits inside one
// LogTimeProvider, so "Log time" opens the same sheet from any screen.
//
// Setup tools that open straight from the address (?tool=), over whatever screen is showing.
// Closing one drops only that param, so the screen underneath stays put.
const SC_TOOLS = ['billing', 'logins'];

export default function SupportCoordination() {
  const navigate = useNavigate();
  const { scId } = useParams();
  const [sp, setSp] = useSearchParams();
  const view = sp.get('view') || 'overview';
  const toolParam = sp.get('tool');
  const tool = SC_TOOLS.includes(toolParam) ? toolParam : null;
  const closeTool = () => setSp((p) => { const n = new URLSearchParams(p); n.delete('tool'); return n; }, { replace: true });

  // SC permissions decide what each screen shows (Log time for others, setup fixes, claiming).
  const scMe = useLoad('/sc/me');
  const perms = (scMe.data && scMe.data.permissions) || [];
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    // Module separation: Plan Management participants (finance-only) never appear in SC pickers.
    api.get('/clients').then((r) => {
      const all = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
      setClients(all.filter((c) => c.service_type !== 'plan_managed'));
    }).catch(() => {});
    api.get('/staff').then((r) => setStaff(Array.isArray(r.data) ? r.data : (r.data?.staff || []))).catch(() => {});
  }, []);
  const openFile = (id) => navigate(`/support-coordination/${id}`);

  let body;
  if (scId) body = <ParticipantFile key={scId} scId={scId} staff={staff} clients={clients} perms={perms} />;
  else if (view === 'engagements') body = <Caseload clients={clients} staff={staff} />;
  else if (view === 'hours') body = <ScHours />;
  else if (view === 'claims') body = <ScClaims />;
  else if (view === 'referrals' || view === 'directory') body = <ScReferrals />;
  else if (view === 'settings') body = <ScSettings canAdmin={perms.includes('admin')} />;
  // No longer in the menu, but still reachable from links (Overview, old bookmarks).
  else if (view === 'myday') body = <TodayView onOpen={openFile} />;
  else if (view === 'calendar') body = <ScCalendarApp staff={staff} onOpenFile={openFile} />;
  else if (view === 'email') body = <MailApp module="sc" />;
  else if (view === 'dashboard') body = <CoordinationDashboard />;
  else body = <ScOverview />;

  return (
    <LogTimeProvider staff={staff} canLogForOthers={perms.includes('view_all_hours')}>
      {/* py only: Layout already pads the content area horizontally. */}
      <div className="py-2 w-full min-w-0">{body}</div>
      {tool === 'billing' && <SettingsModal current={0} onClose={closeTool} onSaved={closeTool} />}
      {tool === 'logins' && <CoordLoginsModal onClose={closeTool} />}
    </LogTimeProvider>
  );
}

// Today: tasks due, key dates in the next two weeks and participants who need a note.
function TodayView({ onOpen }) {
  const navigate = useNavigate();
  const oversight = useLoad('/support-coordination/dashboard/oversight');
  return (
    <>
      <PageHeader title="Today" sub="Tasks due, key dates in the next 14 days, participants who need a note, and recent activity." />
      <MyDay onOpen={onOpen} oversight={oversight.data} onOversight={() => navigate('/support-coordination?view=dashboard&tab=oversight')} />
    </>
  );
}
